import { zodResolver } from '@hookform/resolvers/zod';
import { api } from '@packages/backend/convex/_generated/api';
import {
  type AppStoreUpdatePolicyCvx,
  appStoreUpdatePolicyStrictSchema,
  getUserErrorMessage,
} from '@packages/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import { useQuery } from 'convex-helpers/react';
import { Info } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';
import { AppleAppStoreInfo } from '@/components/AppleAppStoreInfo';
import { EditableVersionField } from '@/components/EditableVersionField';
import { ErrorAlert } from '@/components/ErrorAlert';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ActionButton } from '@/components/ui+/ActionButton';
import { FormWrapper } from '@/components/ui+/FormWrapper';
import { LoadingIcon } from '@/components/ui+/LoadingIcon';
import { handleSubmitErrors } from '@/utils/handleSubmitErrors';

const AppVersionsPage = () => {
  const { data, isPending, error, isError } = useQuery(api.appStoreUpdateManager.getAll);

  if (isPending) return <LoadingIcon />;

  if (isError) {
    return <ErrorAlert message={error.message} title="Error loading app versions" />;
  }

  // Group versions by bundleId
  const groupedVersions = data.reduce<Record<string, typeof data>>((groupedByBundleId, version) => {
    const bundleId = version.bundleId;
    if (!groupedByBundleId[bundleId]) {
      groupedByBundleId[bundleId] = [];
    }
    groupedByBundleId[bundleId].push(version);
    return groupedByBundleId;
  }, {});

  return (
    <div className="space-y-6">
      <Alert className="mb-6 w-full lg:w-1/2" variant="default">
        <Info className="h-4 w-4" />
        <AlertTitle>Update banners</AlertTitle>
        <AlertDescription>
          Apple App Store and Google Play Store versions should be kept in sync with these values. A
          notice will be shown to users when the version set here is greater than the version they
          have installed.
        </AlertDescription>
      </Alert>

      {Object.entries(groupedVersions).map(([bundleId, versions]) => (
        <Card className="w-full lg:w-1/2 mb-8" key={bundleId}>
          <CardHeader>
            <CardTitle className="mb-2">{bundleId}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {versions.map((updatePolicy) => (
                <AppStoreUpdatePolicyForm
                  key={`${updatePolicy.bundleId}-${updatePolicy.platform}`}
                  appStoreUpdatePolicy={updatePolicy}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

const formValuesSchema = appStoreUpdatePolicyStrictSchema;
type FormValues = z.output<typeof formValuesSchema>;

type AppStoreUpdatePolicyFormProps = {
  appStoreUpdatePolicy: AppStoreUpdatePolicyCvx;
};

const AppStoreUpdatePolicyForm = ({ appStoreUpdatePolicy }: AppStoreUpdatePolicyFormProps) => {
  const convex = useConvex();
  const [formKey, setFormKey] = useState(0);

  const form = useForm({
    resolver: zodResolver(formValuesSchema),
    defaultValues: appStoreUpdatePolicy,
  });

  // Subscribe to changes
  useEffect(() => {
    // Ensure un-saved user changes are kept on Convex sync
    form.reset(appStoreUpdatePolicy, { keepDirty: true, keepDirtyValues: true });
  }, [appStoreUpdatePolicy, form]);

  const onValidSubmit = async (formValues: FormValues) => {
    try {
      await convex.mutation(api.appStoreUpdateManager.update, {
        id: appStoreUpdatePolicy._id,
        appStoreUpdatePolicy: formValues,
      });
      // The saved values are the new baseline, so nothing is dirty any more
      form.reset(form.getValues());
      setFormKey((prevKey) => prevKey + 1);
      toast.success('Saved successfully');
    } catch (error) {
      const description = getUserErrorMessage(error);
      toast.error('Error saving!', { description });
    }
  };

  const platformLabel = appStoreUpdatePolicy.platform === 'ios' ? 'iOS' : 'Android';
  const showAppStoreInfo = appStoreUpdatePolicy.platform === 'ios' && appStoreUpdatePolicy.bundleId;

  return (
    <FormWrapper {...form}>
      <form onSubmit={form.handleSubmit(onValidSubmit, handleSubmitErrors)}>
        <div className="mb-4">
          <h3 className="font-semibold text-md mb-4">{platformLabel}</h3>
          <div className="grid gap-4" key={formKey}>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="grow">
                  <EditableVersionField
                    control={form.control}
                    name="targetNativeVersion"
                    label="Target version"
                    forceUpdateName="isUpdateRequired"
                  />
                </div>
                <ActionButton
                  disabled={form.formState.isSubmitting || !form.formState.isDirty}
                  loading={form.formState.isSubmitting}
                  type="submit"
                  size="sm"
                >
                  Save
                </ActionButton>
              </div>

              {showAppStoreInfo && (
                <div className="mt-2">
                  <AppleAppStoreInfo iosBundleId={appStoreUpdatePolicy.bundleId} />
                </div>
              )}
            </div>
          </div>
        </div>
      </form>
    </FormWrapper>
  );
};

export const Route = createFileRoute('/a/app-store-updates')({
  component: AppVersionsPage,
});
